import { useParams, useOutletContext, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef  } from "react";
import { FormGroup, Input, Label, Button, InputGroup, InputGroupText, Modal, ModalHeader, ModalBody, Table } from 'reactstrap';

import { translations } from './../../i18/translations.js';
import { Space } from './../../util/Html.js';
import ContentEditor from './../../util/contenteditor/ContentEditor.js';
import ConfirmDialog from './../../util/ConfirmDialog.js';
import './NewsLetterManagementEdit.css';

const NewsLetterManagementEdit = () => {

    const API_URL = process.env.REACT_APP_SERVICE_API_URL;
    const {user, lang, showAlert, checkAccess} = useOutletContext();
    const {id} = useParams();
    const navigate = useNavigate();

    const [newsLetter, setNewsLetter] = useState(null);
    const [subscriberData, setSubscriberData] = useState(null);
    const [addressList, setAddressList] = useState(null);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [testEmailOpen, setTestEmailOpen] = useState(false);
    const [testEmailAddress, setTestEmailAddress] = useState("");

    const fileInputRef = useRef(null);
    const bodyEditorRef = useRef(null);

    const toggleConfirmDialog = () => setConfirmOpen(!confirmOpen);
    const toggleTestEmailDialog = () => setTestEmailOpen(!testEmailOpen);

    const clearFileSelection = () => {
        fileInputRef.current.value = null;
    };

    const closeAddressListDialog = () => {
        setAddressList(null);
    };

    const fetchNewsletter = async () => {
        try {
            const payload = {
                newsletter: {
                    id: id
                }
            };
            const response = await fetch(`${API_URL}/admin/get_newsletter.php`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (checkAccess(data)) {
                setNewsLetter(data.newsLetter);
                setSubscriberData(data.subscriberData);
            }

        } catch (error) {
            console.error("Error fetching newsletter:", error);
        }
    };

    const fetchNewsletterAddressList = async () => {
        try {
            const payload = {
                newsletter: {
                    id: id
                }
            };
            const response = await fetch(`${API_URL}/admin/get_newsletter_address_list.php`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (checkAccess(data)) {
                setAddressList(data.addressList);
            }
        } catch (error) {
            console.error("Error fetching newsletter:", error);
        }
    };

    const getEditorValueAndCall = async(fnc) => {
        let body = await bodyEditorRef.current.getValue();
        setNewsLetter((prev) => {
            const newContent = {
                ...prev,
                body: body
            };
            fnc(newContent);
            return newContent;
        });
    };

    const save = async(newsLetter) => {
        try {
            const payload = {
                newsLetter: newsLetter
            };
            const response = await fetch(`${API_URL}/admin/save_newsletter.php`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (checkAccess(data)) {
                showAlert(data.alert);
                fetchNewsletter();
            }
        } catch (error) {
            console.error("Error saving newsletter:", error);
        }
    };

    const sendTestMail = async(newsLetter) => {
        let formData = createFilesFormData();
        if (formData === null) {
            return;
        }
        formData.append("newsLetter", JSON.stringify(newsLetter));
        formData.append("testEmailAddress", testEmailAddress);

        try {
            const response = await fetch(`${API_URL}/admin/send_test_newsletter.php`, {
                method: "POST",
                body: formData
            });
            const data = await response.json();
            if (checkAccess(data)) {
                showAlert(data.alert);
            }
        } catch (error) {
            console.error("Error send test newsletter mail:", error);
        }
        toggleTestEmailDialog();
    };

    const sendAllMail = async(newsLetter) => {
        toggleConfirmDialog();
        let formData = createFilesFormData();
        if (formData === null) {
            return;
        }
        formData.append("newsLetter", JSON.stringify(newsLetter));

        try {
            const response = await fetch(`${API_URL}/admin/send_newsletter.php`, {
                method: "POST",
                body: formData
            });
            const data = await response.json();
            if (checkAccess(data)) {
                showAlert(data.alert);
            }
        } catch (error) {
            console.error("Error send newsletter mails:", error);
        }
    };

    const back = () => {
        navigate("/NewsletterManagement");
    };

    const createFilesFormData = () => {
        const files = fileInputRef.current?.files;
        if (!files)
            return null;

        const fd = new FormData();

        Array.from(files).forEach((file) => {
            fd.append("files[]", file);
        });

        return fd;
    };

    const showAddressList = () => {
        fetchNewsletterAddressList();
    };

    const handleTestEmailAddressChange = (e) => {
        setTestEmailAddress(e.target.value);
    };

    const handleNewletterChange = (e) => {
        const {id, value, type, checked} = e.target;
        setNewsLetter((prevData) => ({
                ...prevData,
                [id]: type === 'checkbox' ? checked : value
            }));
    };

    useEffect(() => {
        fetchNewsletter();
    }, [id, lang, user]);

    return (
            <div classname="mbkegy-content">
                <h1 className="mbkegy-content">{translations[lang].newsletter_management}</h1>
            
                {newsLetter && (
                            <>
                            {newsLetter.id > -1 && <FormGroup floating>
                                <InputGroup floating>
                                    <InputGroupText>{translations[lang].newsletter_subscribers}</InputGroupText>
                                    <Input id="subscribers" type="text" rows="3" value={newsLetter.subscribers} disabled /> 
                                    <Button color="primary" onClick={() => showAddressList()}>{translations[lang].address_list}</Button>
                                </InputGroup>
                            </FormGroup>}
                            {newsLetter.id === -1 && <FormGroup floating>
                                <Input type="select" id="subscriberData" value={newsLetter.subscribersAs}>
                                {setSubscriberData && subscriberData.map((f, index) => (
                                                        <option key={index} value={f.newsletterId}>{f.newsletterId === '-1' ? translations[lang].newsletter_all_member : (translations[lang].newsletter_address_copy_from + f.newsletterName)} ({f.subscribers})</option>
                                                                ))}
                                </Input>
                                <Label for="visible">{translations[lang].newsletter_subscribers}</Label>
                            </FormGroup>}
                        
                            <FormGroup switch>
                                <Input id="visible" type="switch" checked={newsLetter.visible} onChange={handleNewletterChange}/>
                                <Label for="visible">{translations[lang].col_visible}</Label>
                            </FormGroup>  
                            <FormGroup floating>
                                <Input id="name_hu" type="text" rows="3" value={newsLetter.name_hu}  onChange={handleNewletterChange}/>
                                <Label for="name_hu">{translations[lang].newsletter_name} [HU]</Label>
                            </FormGroup>
                            <FormGroup floating>
                                <Input id="name_en" type="text" rows="3" value={newsLetter.name_en}  onChange={handleNewletterChange}/>
                                <Label for="name_en">{translations[lang].newsletter_name} [EN]</Label>
                            </FormGroup>
                            <FormGroup floating>
                                <Input id="subject" type="text" rows="3" value={newsLetter.subject} onChange={handleNewletterChange} />
                                <Label for="subject">{translations[lang].newsletter_subject}</Label>
                            </FormGroup>                        
                            <FormGroup>
                                <InputGroup floating>
                                    <Input
                                        id="attachment"
                                        name="file"
                                        type="file"
                                        multiple
                                        innerRef={fileInputRef}
                                        />
                                    <Button color="danger" onClick={clearFileSelection}>{translations[lang].clear_selection}</Button>
                                </InputGroup>
                            </FormGroup>
                        
                            <FormGroup>
                                <ContentEditor ref={bodyEditorRef} id="nlBody" content={newsLetter.body} lang={lang} />
                            </FormGroup>
                        
                            <Button color="primary" onClick={() => getEditorValueAndCall((newsletter) => {
                                            save(newsletter);
                                                })}>{translations[lang].newsletter_save}</Button>
                            <Button color="success" onClick={toggleTestEmailDialog}>{translations[lang].newsletter_send_test_mail}</Button>
                            <Button color="danger" onClick={toggleConfirmDialog}>{translations[lang].newsletter_send_all_mail}</Button>
                            <Button color="warning" onClick={() => back()}>{translations[lang].back}</Button>
                            </>
                            )}
            
                <ConfirmDialog 
                    title={translations[lang].news_letter_all_email}
                    yes={translations[lang].yes}
                    no={translations[lang].cancel}
                    isOpen={confirmOpen} 
                    toggle={toggleConfirmDialog} 
                    onConfirm={() => getEditorValueAndCall((newsletter) => {
                                sendAllMail(newsletter);
                            })} 
                    message={translations[lang].news_letter_all_email_confirm}
                    />
            
            
            
                {/* Modal for displaying addresslist */}
                <Modal isOpen={addressList} toggle={closeAddressListDialog} size="lg">
                    <ModalHeader toggle={closeAddressListDialog}>{translations[lang].address_list}</ModalHeader>
                    <ModalBody className="scrollable-modal-body">            
                        <Table striped className="mbkegy-table">
                            <thead>
                                <tr>
                                    <th style={{width: '50%'}}>{translations[lang].address_list_name}</th>
                                    <th style={{width: '50%'}}>{translations[lang].address_list_email}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {addressList && addressList.map((row) => (
                                <tr key={row.id}>
                                    <td>{row.fullname}</td>
                                    <td>
                                        {row.email.map((e, i) => (
                                                                    <span key={i} className={'address-list-email ' + (e.valid ? 'text-info' : 'text-danger')}>{e.email}<Space /></span>
                                                                                                        ))}
                                                                </td>
                                                            </tr>
                                                            ))}
                            </tbody>
                        </Table>
                    </ModalBody>
                </Modal>
            
                {/* Modal for test email address */}
                <Modal isOpen={testEmailOpen} toggle={toggleTestEmailDialog} size="lg">
                    <ModalHeader toggle={toggleTestEmailDialog}>{translations[lang].news_letter_test_email}</ModalHeader>
                    <ModalBody className="scrollable-modal-body">            
                        {translations[lang].news_letter_test_email_details}
                        <FormGroup floating>
                            <Input id="testEmail" type="text" value={testEmailAddress} onChange={handleTestEmailAddressChange} />
                            <Label for="testEmail">{translations[lang].news_letter_test_email_label}: </Label>
                        </FormGroup>
                        <Button color="primary" onClick={() => getEditorValueAndCall((newsletter) => {
                                                sendTestMail(newsletter);
                                                    })}>{translations[lang].news_letter_test_email_btn}</Button>
                        <Button color="warning" onClick={() => back()}>{translations[lang].back}</Button>
            
                    </ModalBody>
                </Modal>
            </div>
                    );
        };

        export default NewsLetterManagementEdit;